/** The adventure screen: everything around the map canvas.
 *
 * Layout is a CSS grid with four non-overlapping areas - map, status, resources,
 * sidebar. That is not a style choice, it is D8: because the areas do not
 * overlap, the canvas owns every pointer event inside its own area and each
 * panel owns every event inside its own, and nothing anywhere in this package
 * calls `stopPropagation`. The canvas arrives as a `ReactNode` and is rendered
 * as a *sibling* of the panels, never a child of one.
 *
 * The screen owns no simulation state. Camera, selection and hover live in the
 * host, are handed down as props, and come back as callbacks; the observation
 * is read through `useObservation`, which re-reads only when `version` moves.
 */

import { useCallback, useMemo, type CSSProperties, type ReactNode } from "react";
import type { HeroId, Reason, TownId } from "@game/core";
import { GameTheme } from "../theme/components.js";
import { useObservation } from "../hooks/useGameState.js";
import { useHotkeys, type Hotkey } from "../hooks/useHotkeys.js";
import { BitmapText } from "../theme/bitmapText.js";
import { usePhoneLayout } from "../theme/phone.js";
import { CommandBar } from "./CommandBar.js";
import { HeroList } from "./HeroList.js";
import { InfoPanel } from "./InfoPanel.js";
import { Minimap } from "./Minimap.js";
import { PhoneHud } from "./PhoneHud.js";
import { ResourceBar } from "./ResourceBar.js";
import { StatusBar } from "./StatusBar.js";
import { useAdventureMaterial } from "./material.js";
import { TownList } from "./TownList.js";
import { centreCamera, scrollCamera, type CameraView } from "./layout.js";
import { findHero, nextHeroWithMovement } from "./select.js";
import { NO_HOVER, type HeroCapacities, type HoverTarget, type Selection, type StatusState } from "./types.js";

/** Cells per arrow-key press. Two reads as a deliberate nudge; one feels stuck. */
export const SCROLL_STEP_CELLS = 2;

/**
 * Width of the command column, in design pixels, for the adventure map only.
 *
 * The kit's `--hm-sidebar` is 236 design pixels and it resolves through
 * `--hm-scale`, so at 1080p (scale 1.5) the column came out 354px - 18.4% of
 * the window. The classic's command column is about 13%: it is a fixed-size artefact
 * of a fixed-size screen, and every extra percent it takes is a percent of map
 * the player cannot see. The adventure screen is the one place in the kit where
 * that matters enough to override the token, so it overrides it here rather
 * than narrowing every other screen's panel with it.
 *
 * It still scales: 174 design px is 174px at 1366x768 (12.7%) and 261px at
 * 1920x1080 (13.6%), so the text inside grows with the rest of the kit and the
 * column stays the same fraction of the window.
 *
 * Under a browser zoomed OUT it has a floor on the glass: never narrower than
 * 174 DEVICE pixels (`SIDEBAR_CSS` divides by `--hm-dpr`). The readings in it
 * are held to `MIN_DEVICE_CAP` and cannot follow the column down - at 80% on a
 * 1366x768 window the column was 139 device px wide and `Movement 1,600 /
 * 1,600`, eleven rows tall as the floor demands, overprinted its own caption.
 * 174 device px is the narrowest groove those readings were measured to fit
 * at ELEVEN rows, and the floor grows with the window (`deviceCapFloor`: 15
 * rows on a 1080-tall one), so the groove grows with it - 174 * 15 / 11 =
 * 237 device px on a 1080p monitor, which is what `Movement 1,600 / 1,600`
 * in the fifteen-row caption face (185 glyph px plus the groove's chrome)
 * needs. At 100% on that monitor the scaled width (261 CSS px) is still the
 * larger of the two; at 80% it is 209 on the glass and the floor engages.
 */
export const SIDEBAR_DESIGN_PX = 174;

/** The cap floor the 174px column was measured against, in device rows. */
const SIDEBAR_MEASURED_CAP = 11;

/** The `--hm-sidebar` value: the scaled width, floored on the glass. */
export const SIDEBAR_CSS =
  `max(calc(${String(SIDEBAR_DESIGN_PX)}px * var(--hm-scale)), calc(${String(SIDEBAR_DESIGN_PX)}px * var(--hm-cap-floor, ${String(SIDEBAR_MEASURED_CAP)}) / ${String(SIDEBAR_MEASURED_CAP)} / var(--hm-dpr, 1)))`;

/**
 * Why there is no strip under the map.
 *
 * There used to be two status bars: a 1650x36 parchment strip spanning the
 * bottom of the play area, and a plate at the foot of the sidebar. Both were
 * fed from the same `describeHover`, so on an untouched screen both read
 * "Adventure Map" and neither carried anything else - two dead bars naming the
 * screen twice.
 *
 * The classic has one, and it is the sidebar's: the command column's foot is where
 * the info bar lives, and there is no full-width strip under the map at all.
 * So the strip is gone, its grid row is collapsed to zero, the 36 device pixels
 * go back to the map, and `StatusBar` now renders that one plate - with the
 * hovered object, the cost of the proposed move, the kernel's refusal, and,
 * when the pointer is over nothing, the selected hero's own numbers. It is
 * never blank and it never repeats another panel's label.
 */
export const STATUS_BAR_NOTE = "one info plate, at the foot of the command column";

export interface AdventureScreenProps {
  /** The world canvas element, rendered as a sibling of the panels. */
  readonly canvas: ReactNode;
  readonly camera: CameraView;
  readonly onCameraChange: (camera: CameraView) => void;
  readonly selection: Selection;
  readonly onSelectionChange: (selection: Selection) => void;
  readonly status: StatusState;
  readonly onHover?: ((target: HoverTarget) => void) | undefined;
  readonly onEndTurn: () => void;
  /** Non-null greys the end-turn button and explains why in its tooltip. */
  readonly endTurnReason?: Reason | null | undefined;
  readonly onOpenTown?: ((id: TownId) => void) | undefined;
  /** A second press on the selected hero in the roster opens his sheet. */
  readonly onOpenHero?: ((id: HeroId) => void) | undefined;
  readonly capacities?: HeroCapacities | undefined;
  /** The victory condition's argument in words, resolved by the host; the
   *  resource bar prints it as the scenario's aim. */
  readonly conditionSubject?: string | null | undefined;
  /** Merged after the built-in bindings, so a host may add without forking. */
  readonly extraHotkeys?: readonly Hotkey[] | undefined;
  /**
   * Opens the host's system panel - save, load, settings, the way out.
   *
   * A callback and not a rendered node, because the KEY is this screen's to
   * draw. The shell used to float its own button over the map at 10,10, built
   * from the full-screen menu's `.hs-btn` kit: a fourth material and a third
   * type register, wearing a drop shadow, sitting on the terrain the player is
   * trying to click. Handing the screen the action instead means the key is
   * struck from the same bronze as every other key on the map and lands inside
   * the resource bar's own grid area, so the map keeps all of its pixels.
   */
  readonly onOpenMenu?: (() => void) | undefined;
  /** The phone's Info key: the right-press card, for the armed destination. */
  readonly onInfoKey?: (() => void) | undefined;
  /** Whether that card is up, so the key reads as pressed. */
  readonly infoOpen?: boolean | undefined;
  /** Pin the UI scale. Screenshot harness only. */
  readonly scale?: number | undefined;
  readonly className?: string | undefined;
}

/**
 * The system key at the head of the resource bar.
 *
 * Struck from `.hm-adv-key`'s bronze with a stamped label, so it is the same
 * object as the command block and END TURN rather than the menu screen's kit
 * transplanted onto the map.
 */
function SystemKey(props: { readonly onOpen: () => void }): JSX.Element {
  return (
    <button
      type="button"
      className="hm-adv-key hm-adv-syskey"
      onClick={props.onOpen}
      title="Save, load, settings and the way out (Esc)"
    >
      <MenuGlyph />
      <span className="hm-adv-key__label">
        <BitmapText face="roman" scale={1}>Menu</BitmapText>
      </span>
    </button>
  );
}

/** Three bars: the mark every game uses for its own menu. Cut the way the
 *  command keys' marks are - a lit lower wall, a dark rim, a bronze floor. */
function MenuGlyph(): JSX.Element {
  const bars = "M2 3.4 h14 v2.6 h-14 z M2 8.7 h14 v2.6 h-14 z M2 14 h14 v2.6 h-14 z";
  return (
    <svg width="18" height="20" viewBox="0 0 18 20" aria-hidden="true" focusable="false">
      <g transform="translate(0,1.15)">
        <path d={bars} fill="rgba(255, 242, 206, 0.52)" />
      </g>
      <path d={bars} fill="rgba(26, 15, 2, 0.94)" />
    </svg>
  );
}

export function AdventureScreen(props: AdventureScreenProps): JSX.Element {
  const obs = useObservation();
  const {
    camera, onCameraChange, selection, onSelectionChange, onHover, onEndTurn,
  } = props;

  const map = useMemo(
    () => ({ width: obs.map.width, height: obs.map.height, levels: obs.map.levels }),
    [obs.map.width, obs.map.height, obs.map.levels],
  );

  const selectHero = useCallback((id: HeroId) => {
    onSelectionChange({ ...selection, heroId: id });
    const hero = findHero(obs, id);
    if (hero !== null) onCameraChange(centreCamera(camera, map, hero.x, hero.y, hero.z));
  }, [obs, selection, onSelectionChange, camera, onCameraChange, map]);

  const selectTown = useCallback((id: TownId) => {
    onSelectionChange({ ...selection, townId: id });
    const town = obs.towns.find((t) => t.id === id);
    if (town !== undefined) onCameraChange(centreCamera(camera, map, town.x, town.y, town.z));
  }, [obs, selection, onSelectionChange, camera, onCameraChange, map]);

  const nextHero = useCallback(() => {
    const id = nextHeroWithMovement(obs, selection.heroId);
    if (id !== null) selectHero(id);
  }, [obs, selection.heroId, selectHero]);

  const scroll = useCallback((dx: number, dy: number) => {
    onCameraChange(scrollCamera(camera, map, dx * SCROLL_STEP_CELLS, dy * SCROLL_STEP_CELLS));
  }, [camera, map, onCameraChange]);

  const centreOnSelected = useCallback(() => {
    const hero = findHero(obs, selection.heroId);
    if (hero !== null) onCameraChange(centreCamera(camera, map, hero.x, hero.y, hero.z));
  }, [obs, selection.heroId, camera, map, onCameraChange]);

  const hotkeys = useMemo<readonly Hotkey[]>(() => [
    { combo: "space", description: "Next hero with movement left", onFire: nextHero },
    { combo: "e", description: "End turn", onFire: onEndTurn, disabled: props.endTurnReason != null },
    { combo: "c", description: "Centre on the selected hero", onFire: centreOnSelected },
    { combo: "arrowleft", description: "Scroll the map west", onFire: () => scroll(-1, 0), repeats: true },
    { combo: "arrowright", description: "Scroll the map east", onFire: () => scroll(1, 0), repeats: true },
    { combo: "arrowup", description: "Scroll the map north", onFire: () => scroll(0, -1), repeats: true },
    { combo: "arrowdown", description: "Scroll the map south", onFire: () => scroll(0, 1), repeats: true },
    ...(props.extraHotkeys ?? []),
  ], [nextHero, onEndTurn, centreOnSelected, scroll, props.endTurnReason, props.extraHotkeys]);

  useHotkeys(hotkeys);
  useAdventureMaterial();
  const phone = usePhoneLayout();

  const clearHover = useCallback(() => onHover?.(NO_HOVER), [onHover]);
  const menuKey = props.onOpenMenu === undefined ? undefined : <SystemKey onOpen={props.onOpenMenu} />;

  return (
    <GameTheme className={props.className} scale={props.scale} style={{ width: "100%", height: "100%" }}>
      <div
        className={phone.phone ? "hm-adventure hm-adventure--phone" : "hm-adventure"}
        style={{
          "--hm-sidebar": phone.phone ? "0px" : SIDEBAR_CSS,
          // The full-width strip under the map is gone; its row collapses and
          // the map gets the height back. See STATUS_BAR_NOTE.
          "--hm-status-h": "0px",
          ...(phone.phone ? { "--hm-resbar-h": "0px" } : {}),
        } as CSSProperties}
      >
        {/* The canvas: a sibling of every panel below, in its own grid area. */}
        <div className="hm-adventure__map" onPointerLeave={phone.phone ? undefined : clearHover}>
          {props.canvas}
        </div>

        {phone.phone ? (
          <PhoneHud
            camera={camera}
            onCameraChange={onCameraChange}
            selection={selection}
            onSelectHero={selectHero}
            onSelectTown={selectTown}
            status={props.status}
            onHover={onHover}
            onEndTurn={onEndTurn}
            endTurnReason={props.endTurnReason ?? null}
            conditionSubject={props.conditionSubject ?? null}
            onOpenTown={props.onOpenTown}
            onOpenHero={props.onOpenHero}
            capacities={props.capacities}
            hotkeys={hotkeys}
            leading={menuKey}
            onInfoKey={props.onInfoKey}
            infoOpen={props.infoOpen ?? false}
          />
        ) : (
          <>
            <ResourceBar
              className="hm-adventure__res"
              onEndTurn={onEndTurn}
              endTurnReason={props.endTurnReason ?? null}
              conditionSubject={props.conditionSubject ?? null}
              leading={menuKey}
            />

            <div className="hm-adventure__side">
              <Minimap className="hm-side-fixed" camera={camera} onCameraChange={onCameraChange} />
              <HeroList
                className="hm-adv-rackpanel"
                selectedHeroId={selection.heroId}
                onSelect={selectHero}
                onOpen={props.onOpenHero}
                onHover={onHover}
                capacities={props.capacities}
              />
              <TownList
                className="hm-adv-townpanel"
                selectedTownId={selection.townId}
                onSelect={selectTown}
                onOpen={props.onOpenTown}
                onHover={onHover}
              />
              <CommandBar hotkeys={hotkeys} />
              <InfoPanel
                className="hm-adv-sheet"
                selectedHeroId={selection.heroId}
                capacities={props.capacities}
              />
              <StatusBar
                className="hm-side-fixed"
                status={props.status}
                selectedHeroId={selection.heroId}
                capacities={props.capacities}
                small
              />
            </div>
          </>
        )}
      </div>
    </GameTheme>
  );
}
